/**
 * Collapsed store map on /company/: toggle by CTA click.
 *
 * @param {ParentNode} [root=document]
 */
export function initStoreAddresses(root = document) {
  const section = root.querySelector('#store-addresses');
  if (!(section instanceof HTMLElement)) {
    return;
  }
  const isCollapsed = () =>
    section.classList.contains('store-addresses--collapsed') || section.hasAttribute('hidden');

  const refreshMapSize = (attempts = 15) => {
    const map = window.contactMap;
    if (map && map.container && typeof map.container.fitToViewport === 'function') {
      map.container.fitToViewport();
      return;
    }
    if (attempts > 0) {
      setTimeout(() => refreshMapSize(attempts - 1), 200);
    }
  };

  /** Bottom edge of fixed header chrome overlapping the viewport top. */
  const getHeaderOffset = () => {
    const gap = 16;
    let maxBottom = 0;

    const consider = (el) => {
      if (!(el instanceof HTMLElement)) {
        return;
      }

      const style = window.getComputedStyle(el);
      if (style.display === 'none' || style.visibility === 'hidden') {
        return;
      }
      if (parseFloat(style.opacity) === 0) {
        return;
      }

      const rect = el.getBoundingClientRect();
      if (rect.height <= 0) {
        return;
      }

      if (rect.bottom > 0 && rect.top < Math.max(rect.height, 1)) {
        maxBottom = Math.max(maxBottom, rect.bottom);
        return;
      }

      if (style.position === 'fixed' && el.classList.contains('fixedHeader')) {
        maxBottom = Math.max(maxBottom, rect.height);
      }
    };

    if (window.matchMedia('(min-width: 992px)').matches) {
      document
        .querySelectorAll(
          '.fixedHeader, .headerWrapper, #headerfixed.fixed, .header_wrap #header, #header',
        )
        .forEach(consider);

      if (maxBottom <= 0) {
        const fallback = document.querySelector('.fixedHeader') || document.querySelector('#header');
        if (fallback instanceof HTMLElement) {
          maxBottom = fallback.offsetHeight || 0;
        }
      }
    } else {
      document.querySelectorAll('#mobileheader, #bx-panel').forEach(consider);

      if (maxBottom <= 0) {
        const mobile = document.getElementById('mobileheader');
        if (mobile instanceof HTMLElement) {
          maxBottom = mobile.offsetHeight || 0;
        }
      }
    }

    return Math.max(0, Math.round(maxBottom)) + gap;
  };

  const open = () => {
    const wasCollapsed = isCollapsed();

    section.classList.remove('store-addresses--collapsed');
    section.removeAttribute('hidden');

    if (wasCollapsed) {
      requestAnimationFrame(() => refreshMapSize());
    }

    return wasCollapsed;
  };

  const close = () => {
    section.classList.add('store-addresses--collapsed');
    section.setAttribute('hidden', '');
  };

  const scrollToSection = () => {
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const top = section.getBoundingClientRect().top + window.scrollY - getHeaderOffset();

    window.scrollTo({
      top: Math.max(0, top),
      behavior: prefersReduced ? 'auto' : 'smooth',
    });
  };

  const openAndScroll = () => {
    open();
    // Wait for layout after un-hiding, then scroll with header offset.
    requestAnimationFrame(() => {
      requestAnimationFrame(scrollToSection);
    });
  };

  root.querySelectorAll('[data-store-addresses-open]').forEach((el) => {
    if (!(el instanceof HTMLElement)) {
      return;
    }
    if (el.dataset.storeAddressesOpenInited === '1') {
      return;
    }
    el.dataset.storeAddressesOpenInited = '1';

    el.addEventListener('click', (event) => {
      event.preventDefault();

      if (isCollapsed()) {
        openAndScroll();
      } else {
        scrollToSection();
      }
    });
  });

  root.querySelectorAll('[data-store-addresses-close]').forEach((el) => {
    if (!(el instanceof HTMLElement)) {
      return;
    }
    if (el.dataset.storeAddressesCloseInited === '1') {
      return;
    }
    el.dataset.storeAddressesCloseInited = '1';

    el.addEventListener('click', (event) => {
      event.preventDefault();
      if (isCollapsed()) {
        return;
      }
      close();
    });
  });
}
